import { fetchControlRequest } from '@/api/endpoints';
import type { ControlRequestRow } from '@/api/types';

/**
 * Wait for the scheduler to carry out a request. It checks for new ones every
 * ten seconds, so the answer usually arrives within that; after `timeoutMs`
 * the latest state is returned, still PENDING, for the caller to report.
 */
export async function waitForControlRequest(
  id: number,
  { timeoutMs = 60_000, intervalMs = 2_000 }: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<ControlRequestRow> {
  const deadline = Date.now() + timeoutMs;
  let latest = await fetchControlRequest(id);
  while (latest.state === 'PENDING' && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    latest = await fetchControlRequest(id);
  }
  return latest;
}

/**
 * The outcome of a protected sell, in words. The sale is a limit order at most
 * 2% under the best bid, so a thin book can leave part of the position unsold.
 */
export function describeSale(result: Record<string, unknown> | null | undefined): string {
  const state = String(result?.state ?? 'placed').toLowerCase();
  const sold = String(result?.sold_qty ?? '0');
  const unsold = String(result?.unsold_qty ?? '0');
  const remainder = /[1-9]/.test(unsold)
    ? `; ${unsold} could not be sold within 2% of the best bid and is still held`
    : '';
  return `sell order ${state}, ${sold} sold${remainder}`;
}
